import { CountryCode } from "libphonenumber-js";

export type WebMode = "portal" | "routing-manager" | "mvne-portal";

export function isPortalWebMode(mode: WebMode): boolean {
  return mode === "portal";
}

interface RuntimeConfig {
  BRAND: string;
  COUNTRY_CODE: CountryCode;
  CURRENCY_CODE: string;
  TIME_ZONE: string;
  LANGUAGE_LIST: string;
  STRIPE_CONNECTED_ACCOUNT_ID: string;
  CHECKOUT: boolean;
  STOREFRONT: boolean;
  API_URL: string;
  WEB_COOKIE_DOMAIN: string;
  CANONICAL_PREFIX: string;
  ENVIRONMENT: string;
  SIP_DOMAIN: string;
  SIP_SERVER_WS_URL: string;
  REDIRECT_TO_SINGLE_SUBSCRIPTION: boolean;
  LOG_FORMAT: string;
  INTERCOM_APP_ID: string;
  EBBOT_API_URL: string;
  EBBOT_BOT_ID: string;
  EBBOT_OVH: boolean;
  MARKER_PROJECT_ID: string;
  GOOGLE_TAG_CONTAINER: string;
  USE_DEMO_APOLLO_LINKS: boolean;
  CORALOGIX_PUBLIC_KEY: string;
  CORALOGIX_APPLICATION: string;
  CORALOGIX_DOMAIN: string;
  WEB_MODE: WebMode;
  ROUTING_MANAGER_API_URL: string;
  MVNE_PORTAL_URL: string;
  MVNE_PORTAL_REGION: string;
}

// Default runtime values (used as fallback on client-side)
const defaultRuntime: RuntimeConfig = {
  BRAND: "seamless",
  COUNTRY_CODE: "SE" as CountryCode,
  CURRENCY_CODE: "SEK",
  TIME_ZONE: "Europe/Stockholm",
  LANGUAGE_LIST: "sv,en",
  STRIPE_CONNECTED_ACCOUNT_ID: "",
  CHECKOUT: false,
  STOREFRONT: false,
  API_URL: "",
  WEB_COOKIE_DOMAIN: "",
  CANONICAL_PREFIX: "",
  ENVIRONMENT: "development",
  SIP_DOMAIN: "",
  SIP_SERVER_WS_URL: "",
  REDIRECT_TO_SINGLE_SUBSCRIPTION: false,
  LOG_FORMAT: "json",
  INTERCOM_APP_ID: "",
  EBBOT_API_URL: "",
  EBBOT_BOT_ID: "",
  EBBOT_OVH: true,
  MARKER_PROJECT_ID: "",
  GOOGLE_TAG_CONTAINER: "",
  USE_DEMO_APOLLO_LINKS: false,
  CORALOGIX_PUBLIC_KEY: "",
  CORALOGIX_APPLICATION: "",
  CORALOGIX_DOMAIN: "",
  WEB_MODE: "portal",
  ROUTING_MANAGER_API_URL: "",
  MVNE_PORTAL_URL: "",
  MVNE_PORTAL_REGION: "",
};

// Cache for server-side values
let serverCache: RuntimeConfig | null = null;

export const getServerRuntime = (): RuntimeConfig => ({
  BRAND: process.env.WEB_BRAND || defaultRuntime.BRAND,
  COUNTRY_CODE: (
    process.env.COUNTRY || defaultRuntime.COUNTRY_CODE
  ).toUpperCase() as CountryCode,
  CURRENCY_CODE: process.env.CURRENCY_CODE || defaultRuntime.CURRENCY_CODE,
  TIME_ZONE: process.env.TIME_ZONE || defaultRuntime.TIME_ZONE,
  LANGUAGE_LIST: process.env.WEB_LANGUAGE_LIST || defaultRuntime.LANGUAGE_LIST,
  CHECKOUT: process.env.WEB_CHECKOUT === "true" || defaultRuntime.CHECKOUT,
  STOREFRONT:
    process.env.WEB_STOREFRONT === "true" || defaultRuntime.STOREFRONT,
  STRIPE_CONNECTED_ACCOUNT_ID:
    process.env.STRIPE_CONNECTED_ACCOUNT_ID ||
    defaultRuntime.STRIPE_CONNECTED_ACCOUNT_ID,
  API_URL: process.env.API_URL || defaultRuntime.API_URL,
  WEB_COOKIE_DOMAIN:
    process.env.WEB_COOKIE_DOMAIN || defaultRuntime.WEB_COOKIE_DOMAIN,
  CANONICAL_PREFIX:
    process.env.CANONICAL_PREFIX || defaultRuntime.CANONICAL_PREFIX,
  ENVIRONMENT: process.env.ENVIRONMENT || defaultRuntime.ENVIRONMENT,
  SIP_DOMAIN: process.env.IOTCOMMS_SIP_DOMAIN || defaultRuntime.SIP_DOMAIN,
  SIP_SERVER_WS_URL:
    process.env.IOTCOMMS_SIP_SERVER_WS_URL || defaultRuntime.SIP_SERVER_WS_URL,
  REDIRECT_TO_SINGLE_SUBSCRIPTION:
    process.env.REDIRECT_TO_SINGLE_SUBSCRIPTION === "true" ||
    defaultRuntime.REDIRECT_TO_SINGLE_SUBSCRIPTION,
  LOG_FORMAT: process.env.LOG_FORMAT || defaultRuntime.LOG_FORMAT,
  INTERCOM_APP_ID:
    process.env.INTERCOM_APP_ID || defaultRuntime.INTERCOM_APP_ID,
  EBBOT_API_URL: process.env.EBBOT_API_URL || defaultRuntime.EBBOT_API_URL,
  EBBOT_BOT_ID: process.env.EBBOT_BOT_ID || defaultRuntime.EBBOT_BOT_ID,
  EBBOT_OVH:
    process.env.EBBOT_OVH === undefined
      ? defaultRuntime.EBBOT_OVH
      : process.env.EBBOT_OVH === "true",
  MARKER_PROJECT_ID:
    process.env.MARKER_PROJECT_ID || defaultRuntime.MARKER_PROJECT_ID,
  GOOGLE_TAG_CONTAINER:
    process.env.GOOGLE_TAG_CONTAINER || defaultRuntime.GOOGLE_TAG_CONTAINER,
  USE_DEMO_APOLLO_LINKS:
    process.env.USE_DEMO_APOLLO_LINKS === "true" ||
    defaultRuntime.USE_DEMO_APOLLO_LINKS,
  CORALOGIX_PUBLIC_KEY:
    process.env.CORALOGIX_PUBLIC_KEY || defaultRuntime.CORALOGIX_PUBLIC_KEY,
  CORALOGIX_APPLICATION:
    process.env.CORALOGIX_APPLICATION || defaultRuntime.CORALOGIX_APPLICATION,
  CORALOGIX_DOMAIN:
    process.env.CORALOGIX_DOMAIN || defaultRuntime.CORALOGIX_DOMAIN,
  WEB_MODE: (process.env.WEB_MODE === "routing-manager" ||
  process.env.WEB_MODE === "mvne-portal"
    ? process.env.WEB_MODE
    : defaultRuntime.WEB_MODE) as WebMode,
  ROUTING_MANAGER_API_URL:
    process.env.ROUTING_MANAGER_API_URL ||
    defaultRuntime.ROUTING_MANAGER_API_URL,
  MVNE_PORTAL_URL:
    process.env.MVNE_PORTAL_URL || defaultRuntime.MVNE_PORTAL_URL,
  MVNE_PORTAL_REGION:
    process.env.MVNE_PORTAL_REGION || defaultRuntime.MVNE_PORTAL_REGION,
});

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Partial<RuntimeConfig>;
  }
}

function getViteEnvValue(prop: string): string | undefined {
  try {
    return import.meta.env[`VITE_${prop}`];
  } catch {
    return undefined;
  }
}

const configHandler: ProxyHandler<RuntimeConfig> = {
  get(_, prop) {
    const key = prop as string;

    if (typeof window === "undefined") {
      if (!serverCache) {
        serverCache = getServerRuntime();
      }
      return serverCache[prop];
    }

    // window.__RUNTIME_CONFIG__ (production, set by Docker entrypoint)
    const windowConfig = window.__RUNTIME_CONFIG__;
    if (windowConfig && key in windowConfig) {
      return windowConfig[key];
    }

    // import.meta.env.VITE_* (development)
    const viteVal = getViteEnvValue(key);
    if (viteVal !== undefined) {
      if (viteVal === "true") return true;
      if (viteVal === "false") return false;
      return viteVal;
    }

    // Legacy fallback: runtime config injected via cookie
    const match = document.cookie.match(/runtime=([^;]+)/);
    if (match) {
      try {
        const cookieData: RuntimeConfig = JSON.parse(
          decodeURIComponent(match[1]),
        );

        return cookieData[prop];
      } catch {
        // Continue to default fallback
      }
    }

    // Fallback to safe defaults
    return defaultRuntime[prop];
  },
};

export const runtime = new Proxy({} as RuntimeConfig, configHandler);
